import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, Plus, Pencil, Trash2, Mail, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import MasterTable from "@/components/MasterTable";

const emptyForm = { name: "", location: "", pincode: "", contact_email: "", from_email: "", from_name: "" };

export default function BranchesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => { const { data } = await api.get("/branches"); setItems(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      location: b.location || "",
      pincode: b.pincode || "",
      contact_email: b.contact_email || "",
      from_email: b.from_email || "",
      from_name: b.from_name || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/branches/${editing.id}`, form);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", form);
        toast.success("Branch created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"? This is only allowed if it has no users or parcels.`)) return;
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Branch deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const canEdit = (b) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    if (user.role === "company_admin") return b.tenant_id === user.tenant_id;
    if (user.role === "branch_admin") return b.id === user.branch_id;
    return false;
  };
  const canDelete = (b) => (user?.role === "super_admin" || user?.role === "company_admin");
  const canCreate = user?.role === "super_admin" || user?.role === "company_admin";

  const columns = [
    { key: "name", label: "Branch", render: (r) => (
        <div>
          <div className="font-semibold">{r.name}</div>
          {r.from_email && (
            <span className="mono text-[9px] uppercase tracking-widest border border-primary text-primary bg-red-50 px-1.5 py-0.5 rounded inline-flex items-center gap-1 mt-1">
              <Mail size={9} /> custom-from
            </span>
          )}
        </div>
    )},
    { key: "location", label: "Location", render: (r) => (
        <div className="text-xs inline-flex items-center gap-1">
          <MapPin size={11} className="text-muted-foreground" /> {r.location || "—"}
        </div>
    )},
    { key: "pincode", label: "Pincode", className: "mono text-xs" },
    { key: "from_email", label: "From identity", render: (r) => (
        r.from_email
          ? <span className="mono text-[11px]">{r.from_name ? `${r.from_name} ` : ""}&lt;{r.from_email}&gt;</span>
          : <span className="text-muted-foreground text-xs">—</span>
    )},
    { key: "contact_email", label: "Contact", className: "mono text-xs" },
    { key: "created_at", label: "Created", className: "mono text-[11px]", render: (r) => (
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"
    )},
    { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
        <div className="inline-flex items-center gap-2">
          {canEdit(r) && (
            <button data-testid={`branch-edit-${r.id}`} onClick={() => openEdit(r)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold">
              <Pencil size={12} /> Edit
            </button>
          )}
          {canDelete(r) && (
            <button data-testid={`branch-delete-${r.id}`} onClick={() => remove(r)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-semibold">
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
    )},
  ];

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Boxes size={12} /> Locations · Mail identity</div>
          <h1 className="text-4xl font-display mt-1">Branches.</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Each branch can carry its own mail identity — when notifications, POD receipts, or reports are sent from this branch, the "From" line will use that branch's email address (while still authenticating through the tenant SMTP relay).
          </p>
        </div>
        {canCreate && (
          <Button data-testid="branch-new-btn" onClick={openNew} className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
            <Plus size={14} className="mr-2" /> Add branch
          </Button>
        )}
      </header>

      <MasterTable
        columns={columns}
        rows={items}
        searchKeys={["name", "location", "pincode", "contact_email", "from_email", "from_name"]}
        getRowKey={(r) => r.id}
        testidPrefix="branches"
        defaultSort={{ key: "name", dir: "asc" }}
        emptyText="No branches yet."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-md max-h-[90vh] overflow-y-auto" aria-describedby="branch-form-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Branch" : "Add Branch"}</DialogTitle>
            <p id="branch-form-desc" className="text-xs text-muted-foreground">
              A branch is a physical mailroom location. Optional email identity lets outbound notifications appear as being sent from this branch.
            </p>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <F label="Branch name" required v={form.name} set={(v)=>setForm({...form, name:v})} t="branch-name" />
            <F label="Location / address" required v={form.location} set={(v)=>setForm({...form, location:v})} t="branch-location" />
            <F label="Pincode (used for outward rate source)" v={form.pincode} set={(v)=>setForm({...form, pincode:v})} t="branch-pincode" mono />

            <div className="border-t border-border pt-3">
              <div className="overline mb-1 flex items-center gap-1.5"><Mail size={11} /> Mail identity (optional)</div>
              <p className="text-[11px] text-muted-foreground mb-2">
                When set, all emails sent for parcels of this branch use these as the "From" header. Authentication still happens via the tenant SMTP relay — no separate credentials needed.
              </p>
              <F label="From name (display)" v={form.from_name} set={(v)=>setForm({...form, from_name:v})} t="branch-from-name" placeholder="e.g. Acme HQ Mailroom" />
              <F label="From email" v={form.from_email} set={(v)=>setForm({...form, from_email:v})} t="branch-from-email" type="email" placeholder="e.g. hq-mailroom@acme.io" mono />
              <F label="Contact email (for pickup requests)" v={form.contact_email} set={(v)=>setForm({...form, contact_email:v})} t="branch-contact-email" type="email" mono />
            </div>

            <Button data-testid="branch-submit" type="submit" className="w-full rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              {editing ? "Save changes" : "Create branch"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, set, t, type="text", required, placeholder, mono }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Input data-testid={t} value={v || ""} onChange={(e)=>set(e.target.value)} required={required} type={type} placeholder={placeholder}
        className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono text-xs" : ""}`} />
    </label>
  );
}
